import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Award, Trophy } from 'lucide-react';

const SAMPLES = [
  {
    id: 'eddie',
    competition: 'Twin Waters Everyday Heroes',
    badgeColour: 'bg-cyan-100 text-cyan-800 border-cyan-200',
    accent: 'text-cyan-700',
    icon: Award,
    dogName: 'Eddie',
    breed: 'Cattle × Rottweiler',
    age: '4 years',
    ownerLabel: 'Owner',
    ownerName: 'Naomi',
    photo: 'https://customer-assets.emergentagent.com/job_comp-central-1/artifacts/w059v0sp_image0%20%281%29.webp',
    photoAlt: "Eddie the Cattle × Rottweiler with owner Naomi holding his Certificate for Bravery — Blood Donor Hero from SASH",
    story: [
      "Eddie is an outstanding Everyday Hero.",
      "Eddie has helped other dogs make it home to their families — how has he done this? Eddie has been a doggy blood donor! Without Eddie's donation, other dogs would not have made it back to their home and their families would have been devastated.",
      "We are so proud of Eddie for helping give life to other dogs and their families.",
    ],
  },
  {
    id: 'misty',
    competition: "Best Dog Dad (Father's Day)",
    badgeColour: 'bg-amber-100 text-amber-800 border-amber-200',
    accent: 'text-amber-700',
    icon: Trophy,
    dogName: 'Misty',
    breed: 'Border Collie',
    age: '3 years',
    ownerLabel: 'Dog Dad',
    ownerName: 'Marty',
    photo: '/samples/misty-marty.jpg',
    photoAlt: 'Marty and Misty the Border Collie posing with agility and trick dog ribbons',
    story: [
      "I'm Misty and my dad is the best dad in the world! He does so many fun-tivity things with me every week. One week we go do agility — I love agility days because I can run & jump and shoot through tunnels. Other days we go to do all kinds of tricks like standing on blocks, touching paws, jumping through hoops, spinning & twirling and my favourite of all, rolling over; I know over 20 tricks and dad is still teaching me more.",
      "Some days we just go for a walk and maybe play ball, but my favourite day of the month is when he gets my favourite trainer to come pick me up and take me out for an adventure day away from home — we do something different every time I get to go with her.",
      "Ooohh, I almost forgot about going swimming in our pool! It's my Sissy that goes in with me, but Dad throws my retriever toys for me to swim after and he takes credit for it because he says if he hadn't had the pool heated my Sissy wouldn't go in during the winter.",
      "Dad does all of these things for me even though I've been going through my naughty teenage years — but he says that, now that I am growing up and behaving better, he is going to start taking me to the off-leash beach where I can run and jump in the waves.",
    ],
  },
];

const SampleCard = ({ sample }) => {
  const Icon = sample.icon;
  return (
    <Card
      className="overflow-hidden border-slate-200 hover:shadow-xl transition-shadow duration-300 bg-white h-full flex flex-col"
      data-testid={`sample-entry-${sample.id}`}
    >
      {/* Photo — full image always visible (object-contain), 4:5 portrait frame,
          soft gradient backdrop fills any letterbox space. */}
      <div className="relative aspect-[4/5] w-full bg-gradient-to-br from-slate-100 via-white to-slate-100 flex-shrink-0">
        <img
          src={sample.photo}
          alt={sample.photoAlt}
          className="absolute inset-0 w-full h-full object-contain p-2"
          loading="lazy"
          data-testid={`sample-photo-${sample.id}`}
        />
        <div className="absolute top-4 left-4 z-10">
          <Badge className={`${sample.badgeColour} font-semibold shadow-sm`}>
            <Icon className="h-3.5 w-3.5 mr-1" />
            Sample Entry
          </Badge>
        </div>
      </div>

      {/* Story */}
      <CardContent className="p-6 md:p-8 flex flex-col justify-between flex-1">
        <div>
          <p className={`text-xs uppercase tracking-wider font-semibold ${sample.accent} mb-2`}>
            {sample.competition}
          </p>
          <h3 className="text-3xl font-bold text-slate-900 mb-1" data-testid={`sample-dog-name-${sample.id}`}>
            {sample.dogName}
          </h3>
          <p className="text-sm text-slate-500 mb-4">
            {sample.breed} · {sample.age} · {sample.ownerLabel}: <span className="font-medium text-slate-700">{sample.ownerName}</span>
          </p>
          <div className="space-y-3 text-slate-700 leading-relaxed text-[15px]">
            {sample.story.map((para) => (
              <p key={`${sample.id}-${para.slice(0, 40)}`}>{para}</p>
            ))}
          </div>
        </div>
        <div className="mt-6 pt-4 border-t border-slate-100">
          <p className="text-xs text-slate-500 italic">
            This is a sample submission to show the kind of story we love to receive. Photos + heartfelt words → we can't wait to read yours!
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

const SampleEntries = () => (
  <section className="py-12 bg-gradient-to-b from-slate-50 to-white" data-testid="sample-entries-section">
    <div className="container mx-auto px-4">
      <div className="text-center mb-10 max-w-3xl mx-auto">
        <Badge className="bg-cyan-100 text-cyan-800 border-cyan-200 mb-3">Get inspired</Badge>
        <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3">
          Sample Entries — See what we're looking for
        </h2>
        <p className="text-slate-600 text-lg">
          Not sure how to tell your dog's story? Here are two examples of the kind of submissions
          that make our judges' tails wag. Share the moments, the meaning, and the mischief.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-8 max-w-7xl mx-auto items-stretch">
        {SAMPLES.map((sample) => (
          <SampleCard key={sample.id} sample={sample} />
        ))}
      </div>
    </div>
  </section>
);

export default SampleEntries;
